import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/LandingPage.css';
import { auth } from '../firebaseConfig';

const DISCORD_INVITE_URL = process.env.REACT_APP_DISCORD_INVITE;

const TRUSTED_LOGOS = [
  { name: 'OUG', src: '/trusted%20teams/OUG.png', glow: '#f2c94c' },
  { name: 'Undream', src: '/trusted%20teams/undream.png', glow: '#35d36f' },
  { name: 'Amigos', src: '/trusted%20teams/amigos.png', glow: '#ff4f87' },
  { name: 'GodLike', src: '/trusted%20teams/godlike.png', glow: '#f2c94c' },
  { name: 'Team Vitality', src: '/trusted%20teams/Team%20Vitality.png', glow: '#f2c94c' },
  { name: 'S8UL', src: '/trusted%20teams/s8ul.png', glow: '#35d36f' },
];

const HERO_METRICS = [
  { value: '3+', label: 'Regions competing' },
  { value: '5', label: 'Trophies won' },
  { value: '1st', label: 'International CDM Major winner' },
];

const WORKFLOW_FEATURES = [
  {
    step: '01',
    verb: 'Build',
    title: 'Visual Playbooks',
    image: '/assets/MyStrat.png',
    copy: 'Draw routes, mark utility, assign responsibilities, and turn ideas into repeatable setups.',
    points: ['Map-specific planning', 'Player assignments', 'Fast team briefing'],
  },
  {
    step: '02',
    verb: 'Organize',
    title: 'Playbook Library',
    image: '/assets/MyPlaybook.png',
    copy: 'Every setup, every map, every version, and every adjustment lives in one place.',
    points: ['Saved versions', 'Map and mode structure', 'Quick lookup before scrims'],
  },
  {
    step: '03',
    verb: 'Prepare',
    title: 'Team Calendar',
    image: '/assets/MyCalendar.png',
    copy: 'Scrims, reviews, and match days stay visible without digging through Discord.',
    points: ['Shared schedule', 'Review planning', 'Match-day clarity'],
  },
  {
    step: '04',
    verb: 'Adapt',
    title: 'Stats & Scouting',
    image: '/assets/MyStats.png',
    copy: 'Understand your own tendencies and your opponent before the next series starts.',
    points: ['Team trends', 'Player stats', 'Opponent scouting'],
  },
];

const MATCH_DAY_STEPS = [
  { title: 'Plan', primary: 'Calendar', secondary: 'Scouting' },
  { title: 'Brief', primary: 'Playbook', secondary: 'Assignments' },
  { title: 'Scrim', primary: 'Compete', secondary: 'Capture reps' },
  { title: 'Review', primary: 'Stats', secondary: 'Match notes' },
  { title: 'Adapt', primary: 'Update', secondary: 'Save the setup' },
];

const FAQ_ITEMS = [
  {
    question: 'Who is TheBox for?',
    answer: 'Competitive COD Mobile teams that want cleaner scrims, faster prep, and a shared place for strategy.',
  },
  {
    question: 'How much does private beta cost?',
    answer: 'Private beta access is $5.99 USD per team per month. One subscription covers the whole team workspace.',
  },
  {
    question: 'Is team data private?',
    answer: "Your team's playbooks, stats, and scouting data are restricted to authorized members of your team workspace.",
  },
  {
    question: 'Can opponents or other teams see our data?',
    answer: 'No opponent gets access to your workspace data through the public landing page or another team account.',
  },
  {
    question: 'Why Discord onboarding?',
    answer: 'Private beta teams are onboarded manually so setup, feedback, and support stay high quality.',
  },
  {
    question: 'Does TheBox require installation?',
    answer: 'No. TheBox is browser based, so your team can use it from the web.',
  },
  {
    question: 'How many team members can use it?',
    answer: 'The beta is priced per team workspace, not per seat. You can onboard the members your team needs.',
  },
  {
    question: 'Can we cancel anytime?',
    answer: 'Yes. Private beta access is month to month.',
  },
];

const EnhancedLandingPage = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState(null);

  const openDiscord = () => {
    if (DISCORD_INVITE_URL) {
      window.open(DISCORD_INVITE_URL, '_blank', 'noopener,noreferrer');
      return;
    }

    console.warn('REACT_APP_DISCORD_INVITE is not configured.');
    navigate('/login');
  };

  return (
    <div className="landing-page-wrapper">
      <header className="landing-nav">
        <div className="landing-nav-left">
          <button className="landing-logo-button" onClick={() => navigate('/')} aria-label="Go to TheBox home">
            <img src="/assets/theboxlogo.png" alt="TheBox" className="landing-logo" />
          </button>
          <nav className="landing-nav-links" aria-label="Landing page navigation">
            <a href="#product">Product</a>
            <a href="#champions">Champions</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </nav>
        </div>

        <div className="landing-nav-actions">
          <button
            className="secondary-btn"
            onClick={() => {
              if (auth?.currentUser) navigate('/my-strategy');
              else navigate('/login');
            }}
          >
            Login
          </button>
          <button className="primary-btn compact" onClick={openDiscord}>
            Join Beta
          </button>
        </div>
      </header>

      <main className="landing-container">
        <section className="landing-hero">
          <div className="hero-copy">
            <p className="eyebrow">Private beta &middot; Built for competitive COD Mobile teams</p>
            <h1 className="hero-title">
              Built for contenders.
              <span>Proven by champions.</span>
            </h1>
            <p className="hero-subtext">
              The competitive workspace for teams that want cleaner prep, faster reviews, and better calls when the series is on the line.
            </p>
            <div className="hero-buttons">
              <button className="primary-btn" onClick={openDiscord}>
                Join Private Beta <span aria-hidden="true">&rarr;</span>
              </button>
              <a className="text-btn" href="#product">
                See TheBox in action
              </a>
            </div>
            <div className="hero-metrics" aria-label="TheBox competitive impact">
              {HERO_METRICS.map((metric) => (
                <div key={metric.label} className="hero-metric">
                  <strong>{metric.value}</strong>
                  <span>{metric.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-media" aria-label="TheBox product demo">
            <div className="browser-frame hero-browser">
              <div className="browser-bar">
                <span />
                <span />
                <span />
              </div>
              <video
                className="hero-video"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                src="/global%20logos/2025-12-21%2015-46-37.mp4"
              >
                TheBox product demo video.
              </video>
            </div>
          </div>
        </section>

        <section className="trusted-strip" aria-label="Trusted teams">
          <p className="eyebrow">Trusted inside competitive COD Mobile</p>
          <div className="trusted-logos">
            {TRUSTED_LOGOS.map((logo) => (
              <div key={logo.name} className="trusted-logo-card" style={{ '--team-glow': logo.glow }}>
                <img src={logo.src} alt={logo.name} title={logo.name} />
              </div>
            ))}
          </div>
          <p className="trusted-note">Used across China &middot; North America &middot; LATAM &middot; India</p>
        </section>

        <section className="product-spotlight" id="product">
          <p className="eyebrow">Show the product</p>
          <h2>From strategy to scrim in seconds.</h2>
          <p className="section-lead">Build the play. Brief the team. Run the scrim. Review what happened.</p>
          <button className="spotlight-frame" onClick={() => setSelectedFeature(WORKFLOW_FEATURES[0])}>
            <img src="/assets/MyStrat.png" alt="TheBox visual playbook interface" />
          </button>
          <div className="spotlight-actions" aria-label="Visual playbook capabilities">
            <span>Draw routes</span>
            <span>Assign players</span>
            <span>Brief instantly</span>
          </div>
        </section>

        <section className="workflow-section">
          {WORKFLOW_FEATURES.map((feature, index) => (
            <article key={feature.title} className={`workflow-row ${index % 2 === 1 ? 'reverse' : ''}`}>
              <div className="workflow-copy">
                <p className="workflow-step">{feature.step} / {feature.verb}</p>
                <h2>{feature.title}</h2>
                <p>{feature.copy}</p>
                <ul>
                  {feature.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
              <button className="workflow-image" onClick={() => setSelectedFeature(feature)}>
                <img src={feature.image} alt={`${feature.title} screenshot`} />
              </button>
            </article>
          ))}
        </section>

        <section className="match-day-section">
          <div className="match-day-inner">
            <div className="match-day-header">
              <p className="eyebrow">Built for match day</p>
              <h2 className="match-day-title">Plan. Brief. Scrim. Review. Adapt.</h2>
            </div>
            <div className="match-day-timeline" aria-label="TheBox match day workflow">
              {MATCH_DAY_STEPS.map((stage) => (
                <div key={stage.title} className="match-day-stage">
                  <span className="stage-dot" />
                  <h3>{stage.title}</h3>
                  <strong>{stage.primary}</strong>
                  <p>{stage.secondary}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="championship-section" id="champions">
          <p className="eyebrow">Championship lineage</p>
          <h2>Preparation behind the podium.</h2>
          <p className="section-lead">
            From regional champions to international Major winners, competitive teams use TheBox to prepare for matches that matter.
          </p>

          <div className="podium-card">
            <img src="/trusted%20teams/OUG.png" alt="OUG" />
            <div>
              <p className="podium-label">World / Major</p>
              <h3>OUG</h3>
              <strong>2026 CDM Major Champion</strong>
              <span>First international team to win the major event.</span>
            </div>
          </div>

          <div className="champion-grid major-grid">
            <div className="champion-card">
              <img src="/trusted%20teams/undream.png" alt="Undream" />
              <h3>Undream</h3>
              <p>2026 North America Points Major Champion</p>
            </div>
            <div className="champion-card">
              <img src="/trusted%20teams/amigos.png" alt="Amigos" />
              <h3>Amigos</h3>
              <p>2026 Latin America Points Major Champion</p>
            </div>
          </div>

          <div className="champion-grid regional-grid">
            <div className="champion-card small">
              <img src="/trusted%20teams/godlike.png" alt="GodLike" />
              <h3>GodLike</h3>
              <p>2025 CDM S9 Top 3 / India Regional Champion</p>
            </div>
            <div className="champion-card small">
              <img src="/trusted%20teams/Team%20Vitality.png" alt="Team Vitality" />
              <h3>Vitality</h3>
              <p>2024 India Regional Champion</p>
            </div>
            <div className="champion-card small">
              <img src="/trusted%20teams/s8ul.png" alt="S8UL" />
              <h3>S8UL</h3>
              <p>2025 India Regional Runner-Up</p>
            </div>
          </div>
        </section>

        <section className="testimonial-section">
          <div className="testimonial-copy">
            <p className="eyebrow">Real scrim workflow</p>
            <h2>Built around the way teams actually prepare.</h2>
            <p>
              Stop re-explaining the same setup every scrim. Pull it up, brief players, and get straight into useful reps.
            </p>
          </div>
          <div className="testimonial-image">
            <img src="/assets/MyStats.png" alt="TheBox stats dashboard" />
          </div>
        </section>

        <section className="pricing-section" id="pricing">
          <p className="eyebrow">Private beta</p>
          <div className="price-card">
            <h2>Everything your team needs.</h2>
            <div className="price">$5.99</div>
            <p className="price-subtext">per team / month</p>
            <strong className="price-value">One subscription. Your whole team.</strong>
            <ul>
              <li>Visual Playbooks</li>
              <li>Playbook Library</li>
              <li>Team Calendar</li>
              <li>Stats & Scouting</li>
              <li>Personal onboarding</li>
              <li>Direct developer support</li>
            </ul>
            <button className="primary-btn" onClick={openDiscord}>
              Join Private Beta <span aria-hidden="true">&rarr;</span>
            </button>
            <p className="price-note">No installation. Browser based. Personal onboarding.</p>
          </div>
        </section>

        <section className="faq-section" id="faq">
          <p className="eyebrow">FAQ</p>
          <h2>Questions teams ask before joining.</h2>
          <div className="faq-list">
            {FAQ_ITEMS.map((item, index) => (
              <details key={item.question} className="faq-item" open={index < 2}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <footer className="cta-footer">
          <div className="footer-cta">
            <h2>Ready to elevate your strategy?</h2>
            <button className="primary-btn" onClick={openDiscord}>
              Join Private Beta <span aria-hidden="true">&rarr;</span>
            </button>
          </div>
          <div className="footer-grid">
            <div className="footer-brand">
              <img src="/assets/theboxlogo.png" alt="TheBox" />
              <p>Competitive preparation for COD Mobile teams.</p>
            </div>
            <div>
              <h3>Product</h3>
              <a href="#product">Playbooks</a>
              <a href="#product">Stats</a>
              <a href="#product">Calendar</a>
            </div>
            <div>
              <h3>Company</h3>
              <button onClick={openDiscord}>Discord</button>
              <a href="#pricing">Beta Access</a>
            </div>
            <div>
              <h3>Legal</h3>
              <button onClick={() => navigate('/legal/terms')}>Terms</button>
              <button onClick={() => navigate('/legal/privacy')}>Privacy</button>
              <button onClick={() => navigate('/legal/refund')}>Refund Policy</button>
            </div>
          </div>
          <p className="footer-copy">Copyright 2026 TheBox</p>
        </footer>
      </main>

      {selectedFeature && (
        <div className="feature-modal-overlay" onClick={() => setSelectedFeature(null)}>
          <div className="feature-modal large" onClick={(e) => e.stopPropagation()}>
            <img src={selectedFeature.image} alt={selectedFeature.title} />
            <h2>{selectedFeature.title}</h2>
            <p>{selectedFeature.copy}</p>
            <button onClick={() => setSelectedFeature(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedLandingPage;
